import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "../../lib/api";
import { inr, pct, fmtDate, gainClass } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { GrowthArea } from "../../components/charts";
import { PeriodSelector, historyToChart } from "../../components/period";
import StatCard from "../../components/ui/StatCard";

export default function PerformancePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("ALL");

  useEffect(() => {
    api.get("/investor/performance").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  const histData = useMemo(() => (data ? historyToChart(data.history, period) : []), [data, period]);

  if (error) return <AppLayout title="Performance"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Performance"><PageLoader /></AppLayout>;

  const { metrics, cashflows } = data;

  return (
    <AppLayout title="Performance" subtitle="Returns computed from your dated cash flows (XIRR) and holding period (CAGR).">
      <div className="fade-up space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard label="Invested Value" value={inr(metrics.total_invested)} testId="perf-invested" />
          <StatCard label="Current Value" value={inr(metrics.current_value)} testId="perf-current" />
          <StatCard label="Absolute Return" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="perf-abs" />
          <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} sub="Annualised" testId="perf-cagr" />
          <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} sub="Cash-flow adjusted" testId="perf-xirr" />
        </div>

        <div className="glass-card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h3 className="font-heading font-semibold text-white">Portfolio Value Over Time</h3>
            <PeriodSelector period={period} setPeriod={setPeriod} testId="perf-period-selector" />
          </div>
          {histData.length === 0 ? (
            <EmptyState message="Insufficient portfolio history available. Performance visualization will appear as valuation data is recorded." />
          ) : (
            <GrowthArea data={histData} height={340} testId="perf-growth-chart" investedKey="invested" />
          )}
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-6 pt-5 pb-4">
            <h3 className="font-heading font-semibold text-white">Recent Cash Flows</h3>
            <p className="text-xs text-zinc-500 mt-1">Investments are negative flows; redemptions, dividends and interest are positive.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cashflows-table">
              <thead>
                <tr className="border-y border-white/[0.06]">
                  <th className={thCls}>Date</th>
                  <th className={thCls}>Type</th>
                  <th className={`${thCls} text-right`}>Cash Flow</th>
                </tr>
              </thead>
              <tbody>
                {cashflows.slice(0, 12).map((c) => (
                  <tr key={c.id} className={rowCls}>
                    <td className={`${tdCls} tnum text-zinc-300`}>{fmtDate(c.date)}</td>
                    <td className={`${tdCls} text-zinc-500`}>{c.ref_type}</td>
                    <td className={`${tdCls} text-right tnum font-semibold ${gainClass(c.amount)}`}>
                      {c.amount >= 0 ? "+" : "−"}{inr(Math.abs(c.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
